import { ReactNode } from "react";
import { cn } from "lib/utils";
import { DraggableCardBody, DraggableCardContainer } from "components/DraggableCard";

const CLIENT_PHOTO_COUNT = 21;

const clientPhotos: string[] = Array.from({ length: CLIENT_PHOTO_COUNT }, (_, i) => {
  const n = i + 1;
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const mod = require(`../assets/customers/client_${n}.avif`);
  return (typeof mod === "string" ? mod : mod.default) as string;
});

const CARD_ROTATIONS = [-8, 5, -4, 7, -6, 3, -9, 6, -2, 8, -5, 4, -7, 2, -3, 9, -4, 6, -8, 3, -6];
const CARD_COLS = 5;
const CARD_ROW_HEIGHT = 200;

const clientCards = clientPhotos.map((img, i) => {
  const col = i % CARD_COLS;
  const row = Math.floor(i / CARD_COLS);
  const leftPct = 4 + col * (92 / (CARD_COLS - 1)) + (((i * 37) % 11) - 5);
  const top = 20 + row * CARD_ROW_HEIGHT + (((i * 53) % 41) - 20);
  return {
    img,
    rotate: CARD_ROTATIONS[i % CARD_ROTATIONS.length],
    top,
    leftPct,
  };
});

interface MarqueeProps {
  className?: string;
  reverse?: boolean;
  pauseOnHover?: boolean;
  children: ReactNode;
  repeat?: number;
  vertical?: boolean;
}

const Marquee = ({
  className = "",
  reverse = false,
  pauseOnHover = false,
  children,
  repeat = 4,
  vertical = false,
}: MarqueeProps) => {
  return (
    <div
      className={cn(
        "group flex select-none gap-4 overflow-hidden",
        vertical ? "h-full flex-col" : "w-full flex-row",
        className
      )}
    >
      {Array(repeat)
        .fill(0)
        .map((_, i) => (
          <div
            key={i}
            className={cn(
              "flex gap-4",
              vertical
                ? reverse
                  ? "animate-marquee-vertical-reverse flex-col"
                  : "animate-marquee-vertical flex-col"
                : reverse
                ? "animate-marquee-horizontal-reverse flex-row"
                : "animate-marquee-horizontal flex-row",
              pauseOnHover && "group-hover:[animation-play-state:paused]"
            )}
          >
            {children}
          </div>
        ))}
    </div>
  );
};

interface Testimonial {
  id: number;
  name: string;
  text: string;
  rating: number;
  timeAgo: string;
  platform: string;
}

const testimonials: Testimonial[] = [
  {
    id: 1,
    name: "John Ric Cruz Tanael",
    text: "Very accomodating seller and will give you lots of advice on proper care, he will even allow you to handle his trained parrot so you can play and take pictures with them",
    rating: 5,
    timeAgo: "5 years ago",
    platform: "Google Reviews",
  },
  {
    id: 2,
    name: "Kara Ayroso",
    text: "So the reviews said YOU CAN PLAY WITH THEM REALLY? YOUR MY NEW TOP FAVORITE PET STORE",
    rating: 5,
    timeAgo: "10 months ago",
    platform: "Google Reviews",
  },
  {
    id: 3,
    name: "Maria Santos",
    text: "Amazing experience! The staff is knowledgeable and the birds are well-cared for. Highly recommend!",
    rating: 4,
    timeAgo: "2 months ago",
    platform: "Google Reviews",
  },
  {
    id: 4,
    name: "David Chen",
    text: "Best pet store in town! Great selection of birds and excellent customer service.",
    rating: 4,
    timeAgo: "6 months ago",
    platform: "Google Reviews",
  },
  {
    id: 5,
    name: "Sarah Johnson",
    text: "The owner is passionate about birds and it shows. Great advice and healthy birds!",
    rating: 5,
    timeAgo: "1 year ago",
    platform: "Google Reviews",
  },
  {
    id: 6,
    name: "Mike Rodriguez",
    text: "Fantastic place! The birds are beautiful and well-trained. Will definitely come back!",
    rating: 4,
    timeAgo: "8 months ago",
    platform: "Google Reviews",
  },
];

const AVATAR_COLORS = ["#f5a623", "#4285f4", "#34a853", "#ea4335", "#a142f4", "#00acc1"];

const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase())
    .join("");

const avatarColorFor = (name: string) => {
  const sum = name.split("").reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return AVATAR_COLORS[sum % AVATAR_COLORS.length];
};

const TestimonialCard = ({ testimonial }: { testimonial: Testimonial }) => (
  <div className="w-60 flex-shrink-0 cursor-pointer rounded-2xl border border-black/5 bg-white p-6 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1),0_2px_4px_-1px_rgba(0,0,0,0.06)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_10px_25px_-3px_rgba(0,0,0,0.1),0_4px_6px_-2px_rgba(0,0,0,0.05)] max-[768px]:w-44 max-[768px]:p-4 max-[480px]:w-36 max-[480px]:p-3">
    <div className="mb-4 flex items-start justify-between">
      <div className="flex gap-0.5">
        {[...Array(Math.floor(testimonial.rating))].map((_, i) => (
          <span key={i} className="text-sm">
            ⭐
          </span>
        ))}
      </div>
      <div className="flex items-center gap-1 rounded-md bg-[#f3f4f6] px-2 py-1 text-xs">
        <span className="flex h-4 w-4 items-center justify-center rounded-sm bg-[#4285f4] text-[10px] font-bold text-white">
          G
        </span>
        <span className="font-medium text-[#6b7280]">
          {testimonial.platform}
        </span>
      </div>
    </div>
    <p className="mb-4 text-sm leading-relaxed text-[#374151] max-[480px]:text-xs">
      "{testimonial.text}"
    </p>
    <div className="border-t border-[#f3f4f6] pt-4">
      <div className="flex items-center gap-3">
        {/* TODO: swap for a real client photo once provided — falls back to initials */}
        <span
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
          style={{ backgroundColor: avatarColorFor(testimonial.name) }}
        >
          {getInitials(testimonial.name)}
        </span>
        <div className="flex flex-col gap-1">
          <h4 className="m-0 text-sm font-semibold text-[#1f2937]">
            {testimonial.name}
          </h4>
          <p className="m-0 text-xs text-[#9ca3af]">{testimonial.timeAgo}</p>
        </div>
      </div>
    </div>
  </div>
);

const Testimonials = () => {
  const firstRow = testimonials.slice(0, Math.ceil(testimonials.length / 2));
  const secondRow = testimonials.slice(Math.ceil(testimonials.length / 2));

  return (
    <section className="relative bg-gradient-to-br from-[#f8fafc] to-[#f4f7f9] py-20 md:py-28">
      <div className="container">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-2 lg:items-start">
          {/* Google Reviews */}
          <div>
            <div className="mb-10 text-center lg:text-left">
              <h2 className="mb-4 font-sf-pro text-3xl font-bold text-[#1a202c]">
                Google Reviews
              </h2>
              <p className="mx-auto max-w-[600px] font-sf-pro text-base text-[#64748b] lg:mx-0">
                Authentic feedback from our valued customers
              </p>
            </div>

            <div className="relative flex w-full flex-col items-center justify-center gap-4 overflow-hidden">
              <Marquee pauseOnHover className="w-full">
                {firstRow.map((testimonial) => (
                  <TestimonialCard key={testimonial.id} testimonial={testimonial} />
                ))}
              </Marquee>
              <Marquee reverse pauseOnHover className="w-full">
                {secondRow.map((testimonial) => (
                  <TestimonialCard key={testimonial.id} testimonial={testimonial} />
                ))}
              </Marquee>

              <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-1/4 bg-gradient-to-r from-[#f8fafc] to-transparent" />
              <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-1/4 bg-gradient-to-l from-[#f8fafc] to-transparent" />
            </div>
          </div>

          {/* Visitor & client photos */}
          <div>
            <div className="mb-8 text-center lg:text-left">
              <h3 className="mb-4 font-sf-pro text-3xl font-bold text-[#1a202c]">
                Happy Visitors &amp; Clients
              </h3>
              <p className="mx-auto max-w-[500px] font-sf-pro text-base text-[#64748b] lg:mx-0">
                Moments shared with the families who visit our aviary
              </p>
            </div>
            <DraggableCardContainer className="relative h-[420px] w-full overflow-x-clip overflow-y-auto md:h-[560px]">
              {clientCards.map((card, i) => (
                <DraggableCardBody
                  key={i}
                  rotate={card.rotate}
                  className="min-h-40 w-32 p-3"
                  style={{ position: "absolute", top: card.top, left: `${card.leftPct}%` }}
                >
                  <img
                    src={card.img}
                    alt="Mysher Aviary visitor"
                    className="pointer-events-none relative z-10 h-full w-full rounded object-cover"
                  />
                </DraggableCardBody>
              ))}
            </DraggableCardContainer>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
